import React, { useState, useEffect, useRef, useMemo } from 'react';
import useTokenStore from '../../stores/tokens';
import { compatApi } from '../../utils/Intercepter';
import { toast } from 'react-toastify';
import toastConfig from '../../const/toastConfig';
import { PURPLE, hexToRgba } from '../../utils/designUtils';

const LEAVE_TYPES = [
    { value: 'ANNUAL',  label: '연차' },
    { value: 'HALF_AM', label: '반차 (오전)' },
    { value: 'HALF_PM', label: '반차 (오후)' },
    { value: 'PUBLIC',  label: '공가' },
    { value: 'SICK',    label: '병가' },
    { value: 'SUMMER',  label: '하계휴가' },
    { value: 'OTHER',   label: '기타' },
];

const inputS = {
    width: '100%', height: 34, borderRadius: 8, border: '1.5px solid #ececf2',
    background: '#fafaff', padding: '0 10px', fontSize: 13, color: '#1f1d2e',
    outline: 'none', fontFamily: 'inherit', boxShadow: 'none', boxSizing: 'border-box',
};
const labelS = { display: 'block', fontSize: 13, fontWeight: 600, color: '#3a3849', marginBottom: 5 };
const fieldS = { marginBottom: 14 };
const reqS = { color: '#E5453D' };

export default function VacationEditModal({ show, onHide, vacationId, onAction }) {
    const { user } = useTokenStore();

    const [loadingDetail, setLoadingDetail] = useState(false);
    const [submitting,    setSubmitting]    = useState(false);
    const [saving,        setSaving]        = useState(false);
    const [deleting,      setDeleting]      = useState(false);
    const [copying,       setCopying]       = useState(false);

    const [form, setForm] = useState(null);
    const [approvers,      setApprovers]      = useState([]);
    const [approverSearch, setApproverSearch] = useState('');
    const [showApproverList, setShowApproverList] = useState(false);
    const [agentSearch,    setAgentSearch]    = useState('');
    const [showAgentList,  setShowAgentList]  = useState(false);
    const [members,        setMembers]        = useState([]);
    const [holidays,       setHolidays]       = useState(new Set());

    const approverDropRef = useRef(null);
    const agentDropRef    = useRef(null);

    useEffect(() => {
        if (!show || !vacationId) { setForm(null); return; }
        let cancelled = false;
        setLoadingDetail(true);
        compatApi.get(`/approval/${vacationId}`)
            .then(res => {
                if (cancelled) return;
                const d = res.data;
                setForm({
                    orgCode:            d.organizationCode || '',
                    leaveType:          d.leaveType        || 'ANNUAL',
                    startDt:            d.startDt          ? String(d.startDt) : '',
                    endDt:              d.endDt            ? String(d.endDt)   : '',
                    reason:             d.reason           || '',
                    remark:             d.remark           || '',
                    agentEmail:         d.agentEmail       || '',
                    emergencyContact:   d.emergencyContact || '',
                    emergencyRelation:  d.emergencyRelation || '',
                    notiApprovalStatus: true,
                    notiDayBefore:      false,
                    notiApprover:       true,
                });
                setApprovers(
                    (d.approvers || [])
                        .sort((a, b) => a.approverOrder - b.approverOrder)
                        .map(a => ({ userEmail: a.approverEmail, userName: a.approverName || a.approverEmail }))
                );
                return compatApi.post('/org/find/orgUser', {
                    organizationCode: d.organizationCode, searchWord: '', searchOption: 'name',
                });
            })
            .then(res => {
                if (cancelled || !res) return;
                setMembers((res.data || []).filter(m => m.userEmail !== user?.userEmail));
            })
            .catch(() => { if (!cancelled) setForm(null); })
            .finally(() => { if (!cancelled) setLoadingDetail(false); });
        return () => { cancelled = true; };
    }, [show, vacationId, user?.userEmail]);

    useEffect(() => {
        if (!form?.startDt || !form?.endDt || form.endDt < form.startDt) { setHolidays(new Set()); return; }
        let cancelled = false;
        compatApi.get('/calendar/holidays', { params: { startDt: form.startDt, endDt: form.endDt } })
            .then(res => { if (!cancelled) setHolidays(new Set(res.data || [])); })
            .catch(() => { if (!cancelled) setHolidays(new Set()); });
        return () => { cancelled = true; };
    }, [form?.startDt, form?.endDt]);

    useEffect(() => {
        const handle = (e) => {
            if (approverDropRef.current && !approverDropRef.current.contains(e.target)) setShowApproverList(false);
            if (agentDropRef.current    && !agentDropRef.current.contains(e.target))    setShowAgentList(false);
        };
        document.addEventListener('mousedown', handle);
        return () => document.removeEventListener('mousedown', handle);
    }, []);

    const filteredAgents = useMemo(() => {
        const kw = agentSearch.trim().toLowerCase();
        if (!kw) return members;
        return members.filter(m =>
            (m.userName || '').toLowerCase().includes(kw) || m.userEmail.toLowerCase().includes(kw)
        );
    }, [members, agentSearch]);

    const filteredMembers = useMemo(() => {
        const added = new Set(approvers.map(a => a.userEmail));
        const kw = approverSearch.trim().toLowerCase();
        return members.filter(m =>
            !added.has(m.userEmail) &&
            (kw === '' || (m.userName || '').toLowerCase().includes(kw) || m.userEmail.toLowerCase().includes(kw))
        );
    }, [members, approvers, approverSearch]);

    const addApprover = (member) => {
        if (approvers.some(a => a.userEmail === member.userEmail)) {
            toast.warn('이미 추가된 결재자입니다.', toastConfig); return;
        }
        setApprovers(prev => [...prev, { userEmail: member.userEmail, userName: member.userName }]);
        setApproverSearch(''); setShowApproverList(false);
    };
    const removeApprover = (email) => setApprovers(prev => prev.filter(a => a.userEmail !== email));
    const moveApprover = (index, dir) => {
        const next = index + dir;
        if (next < 0 || next >= approvers.length) return;
        setApprovers(prev => { const arr = [...prev]; [arr[index], arr[next]] = [arr[next], arr[index]]; return arr; });
    };

    const workingDays = useMemo(() => {
        if (!form?.startDt || !form?.endDt) return null;
        const [sy, sm, sd] = form.startDt.split('-').map(Number);
        const [ey, em, ed] = form.endDt.split('-').map(Number);
        const start = new Date(sy, sm - 1, sd);
        const end   = new Date(ey, em - 1, ed);
        if (end < start) return -1;
        let count = 0;
        const cur = new Date(start);
        while (cur <= end) {
            const dow = cur.getDay();
            if (dow !== 0 && dow !== 6) {
                const ds = `${cur.getFullYear()}-${String(cur.getMonth()+1).padStart(2,'0')}-${String(cur.getDate()).padStart(2,'0')}`;
                if (!holidays.has(ds)) count++;
            }
            cur.setDate(cur.getDate() + 1);
        }
        return count;
    }, [form?.startDt, form?.endDt, holidays]);

    const buildPayload = (status) => ({
        organizationCode:  form.orgCode,
        leaveType:         form.leaveType,
        startDt:           form.startDt,
        endDt:             form.endDt,
        reason:            form.reason,
        remark:            form.remark || null,
        agentEmail:        form.agentEmail || null,
        emergencyContact:  form.emergencyContact || null,
        emergencyRelation: form.emergencyRelation || null,
        approvers:         approvers.map((a, i) => ({ userEmail: a.userEmail, order: i + 1 })),
        notiApprovalStatus: form.notiApprovalStatus,
        notiDayBefore:      form.notiDayBefore,
        notiApprover:       form.notiApprover,
        status,
    });

    const validate = (requireApprovers) => {
        if (!form.startDt || !form.endDt) { toast.warn('시작일과 종료일을 입력하세요.', toastConfig); return false; }
        if (workingDays < 0) { toast.warn('종료일이 시작일보다 앞에 있습니다.', toastConfig); return false; }
        if (requireApprovers && !form.reason.trim()) { toast.warn('사유를 입력하세요.', toastConfig); return false; }
        if (requireApprovers && approvers.length === 0) { toast.warn('결재자를 한 명 이상 추가하세요.', toastConfig); return false; }
        return true;
    };

    const handleSave = async () => {
        if (!validate(false)) return;
        setSaving(true);
        try {
            await compatApi.put(`/approval/${vacationId}`, buildPayload('DRAFT'));
            toast.success('임시 저장되었습니다.', toastConfig);
            onHide(); onAction?.();
        } catch (e) { toast.error(e?.response?.data?.message || '저장에 실패했습니다.', toastConfig); }
        finally { setSaving(false); }
    };
    const handleSubmit = async () => {
        if (!validate(true)) return;
        if (!window.confirm('제출 후에는 문서를 수정하거나 취소할 수 없습니다.\n제출하시겠습니까?')) return;
        setSubmitting(true);
        try {
            await compatApi.put(`/approval/${vacationId}`, buildPayload('PENDING'));
            toast.success('휴가 신청서가 제출되었습니다.', toastConfig);
            onHide(); onAction?.();
        } catch (e) { toast.error(e?.response?.data?.message || '제출에 실패했습니다.', toastConfig); }
        finally { setSubmitting(false); }
    };
    const handleDelete = async () => {
        if (!window.confirm('임시저장 문서를 삭제하시겠습니까?')) return;
        setDeleting(true);
        try {
            await compatApi.del(`/approval/${vacationId}`);
            toast.success('삭제되었습니다.', toastConfig);
            onHide(); onAction?.();
        } catch { toast.error('삭제에 실패했습니다.', toastConfig); }
        finally { setDeleting(false); }
    };
    const handleCopy = async () => {
        setCopying(true);
        try {
            await compatApi.post('/approval/vacation', buildPayload('DRAFT'));
            toast.success('복사본이 임시 저장되었습니다.', toastConfig);
            onHide(); onAction?.();
        } catch (e) { toast.error(e?.response?.data?.message || '복사에 실패했습니다.', toastConfig); }
        finally { setCopying(false); }
    };

    const busy = saving || submitting || deleting || copying;

    const memberItem = (m, onClick) => (
        <div key={m.userEmail} onMouseDown={onClick}
            style={{
                padding: '7px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                fontSize: '0.85rem', display: 'flex', alignItems: 'center', gap: 8,
            }}
            onMouseEnter={e => e.currentTarget.style.backgroundColor = '#f0eeff'}
            onMouseLeave={e => e.currentTarget.style.backgroundColor = '#fff'}>
            <div style={{
                width: 28, height: 28, borderRadius: '50%', backgroundColor: '#526D82', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.75rem', fontWeight: 600, flexShrink: 0,
            }}>{(m.userName || '?').charAt(0)}</div>
            <div>
                <div style={{ fontWeight: 600, color: '#222' }}>{m.userName}</div>
                <div style={{ fontSize: '0.75rem', color: '#888' }}>{m.userEmail}</div>
            </div>
            {(m.position || m.department) && (
                <div style={{ marginLeft: 'auto', fontSize: '0.72rem', color: '#aaa' }}>
                    {[m.department, m.position].filter(Boolean).join(' · ')}
                </div>
            )}
        </div>
    );

    if (!show) return null;

    return (
        <div style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            background: 'rgba(0,0,0,0.5)', zIndex: 1050,
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px',
        }} onClick={onHide}>
            <div style={{
                background: 'white', borderRadius: 16,
                boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
                width: '100%', maxWidth: 680,
                maxHeight: '90vh', display: 'flex', flexDirection: 'column',
            }} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div style={{
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                    padding: '16px 20px', borderBottom: '1px solid #ececf2', flexShrink: 0,
                }}>
                    <span style={{ fontSize: 15, fontWeight: 700, color: '#1f1d2e' }}>휴가 신청서 수정</span>
                    <button onClick={onHide} style={{
                        border: 'none', background: 'none', fontSize: 18, cursor: 'pointer', color: '#7a7989', padding: 0,
                    }}>✕</button>
                </div>

                {/* Body */}
                <div style={{ padding: '20px', overflowY: 'auto', flex: 1 }}>
                    {loadingDetail || !form ? (
                        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px 0' }}>
                            <div style={{ width: 24, height: 24, borderRadius: '50%', border: `2px solid ${hexToRgba(PURPLE, 0.2)}`, borderTopColor: PURPLE, animation: 'spin 0.7s linear infinite' }}/>
                            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
                        </div>
                    ) : (
                        <>
                            {/* 조직 */}
                            <div style={fieldS}>
                                <label style={labelS}>조직 <span style={reqS}>*</span></label>
                                <select value={form.orgCode} disabled
                                    style={{ ...inputS, background: '#f5f4fa', cursor: 'not-allowed' }}>
                                    {(user?.orgUserList || []).filter(o => o.isApproved).map(o => (
                                        <option key={o.organizationCode} value={o.organizationCode}>
                                            {o.organizationName} ({o.organizationCode})
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* 결재선 */}
                            <div style={fieldS}>
                                <label style={labelS}>결재선 <span style={reqS}>*</span></label>
                                <div style={{
                                    display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4, marginBottom: 8,
                                    padding: '10px 12px', backgroundColor: '#f8f9fa',
                                    borderRadius: 6, border: '1px solid #dee2e6', minHeight: 52,
                                }}>
                                    <div style={{
                                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                                        padding: '4px 10px', borderRadius: 4, backgroundColor: '#526D82',
                                        color: '#fff', fontSize: '0.75rem', minWidth: 60, textAlign: 'center',
                                    }}>
                                        <span style={{ fontSize: '0.65rem', opacity: 0.8 }}>기안자</span>
                                        <span style={{ fontWeight: 600 }}>{user?.userName || '-'}</span>
                                    </div>
                                    {approvers.length === 0 && <span style={{ fontSize: '0.8rem', color: '#aaa', marginLeft: 4 }}>결재자를 추가하세요</span>}
                                    {approvers.map((a, i) => (
                                        <React.Fragment key={a.userEmail}>
                                            <span style={{ color: '#aaa', fontSize: '1rem' }}>›</span>
                                            <div style={{
                                                display: 'flex', flexDirection: 'column', alignItems: 'center',
                                                padding: '4px 8px 6px', borderRadius: 4,
                                                backgroundColor: '#fff', border: '1px solid #5c9894',
                                                fontSize: '0.75rem', minWidth: 64, textAlign: 'center', position: 'relative',
                                            }}>
                                                <span style={{ fontSize: '0.65rem', color: '#5c9894' }}>결재자 {i + 1}</span>
                                                <span style={{ fontWeight: 600, color: '#333' }}>{a.userName}</span>
                                                <div style={{ display: 'flex', gap: 2, marginTop: 3 }}>
                                                    <button type="button" onClick={() => moveApprover(i, -1)} disabled={i === 0}
                                                        style={{ width: 18, height: 14, border: '1px solid #ccc', borderRadius: 2, backgroundColor: i === 0 ? '#f0f0f0' : '#fff', color: i === 0 ? '#ccc' : '#555', fontSize: '0.6rem', cursor: i === 0 ? 'default' : 'pointer', padding: 0 }}>◀</button>
                                                    <button type="button" onClick={() => moveApprover(i, 1)} disabled={i === approvers.length - 1}
                                                        style={{ width: 18, height: 14, border: '1px solid #ccc', borderRadius: 2, backgroundColor: i === approvers.length - 1 ? '#f0f0f0' : '#fff', color: i === approvers.length - 1 ? '#ccc' : '#555', fontSize: '0.6rem', cursor: i === approvers.length - 1 ? 'default' : 'pointer', padding: 0 }}>▶</button>
                                                </div>
                                                <button type="button" onClick={() => removeApprover(a.userEmail)}
                                                    style={{ position: 'absolute', top: -6, right: -6, width: 16, height: 16, borderRadius: '50%', border: 'none', backgroundColor: '#dc3545', color: '#fff', fontSize: '0.6rem', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                                            </div>
                                        </React.Fragment>
                                    ))}
                                </div>
                                <div ref={approverDropRef} style={{ position: 'relative' }}>
                                    <input placeholder="이름 또는 이메일로 검색하여 결재자 추가"
                                        value={approverSearch}
                                        onChange={e => { setApproverSearch(e.target.value); setShowApproverList(true); }}
                                        onFocus={() => setShowApproverList(true)}
                                        autoComplete="off"
                                        style={{ ...inputS }}
                                        onFocus2={e => e.target.style.borderColor = PURPLE}
                                        onBlur={e => e.target.style.borderColor = '#ececf2'}/>
                                    {showApproverList && (
                                        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, backgroundColor: '#fff', border: '1px solid #dee2e6', borderTop: 'none', borderRadius: '0 0 4px 4px', boxShadow: '0 4px 8px rgba(0,0,0,0.08)', maxHeight: 200, overflowY: 'auto' }}>
                                            {filteredMembers.length === 0
                                                ? <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#aaa', textAlign: 'center' }}>{members.length === 0 ? '조직원 정보를 불러오는 중...' : '검색 결과가 없습니다.'}</div>
                                                : filteredMembers.map(m => memberItem(m, () => addApprover(m)))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* 신청자 */}
                            <div style={fieldS}>
                                <label style={labelS}>신청자</label>
                                <input value={user?.userName || ''} disabled style={{ ...inputS, background: '#f5f4fa', cursor: 'not-allowed' }}/>
                            </div>

                            {/* 휴가 구분 */}
                            <div style={fieldS}>
                                <label style={labelS}>휴가 구분 <span style={reqS}>*</span></label>
                                <select value={form.leaveType} onChange={e => setForm(f => ({ ...f, leaveType: e.target.value }))}
                                    style={{ ...inputS }}
                                    onFocus={e => e.target.style.borderColor = PURPLE}
                                    onBlur={e => e.target.style.borderColor = '#ececf2'}>
                                    {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                                </select>
                            </div>

                            {/* 날짜 */}
                            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                                <div style={{ flex: 1 }}>
                                    <label style={labelS}>시작일 <span style={reqS}>*</span></label>
                                    <input type="date" value={form.startDt}
                                        onChange={e => setForm(f => ({ ...f, startDt: e.target.value }))}
                                        style={{ ...inputS }}
                                        onFocus={e => e.target.style.borderColor = PURPLE}
                                        onBlur={e => { e.target.style.borderColor = '#ececf2'; if (e.target.value) setForm(f => ({ ...f, endDt: f.endDt || e.target.value })); }}/>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={labelS}>종료일 <span style={reqS}>*</span></label>
                                    <input type="date" value={form.endDt}
                                        onChange={e => setForm(f => ({ ...f, endDt: e.target.value }))}
                                        style={{ ...inputS }}
                                        onFocus={e => e.target.style.borderColor = PURPLE}
                                        onBlur={e => e.target.style.borderColor = '#ececf2'}/>
                                </div>
                            </div>
                            {workingDays !== null && (
                                <div style={{ marginBottom: 14, textAlign: 'right', fontSize: '0.82rem', color: workingDays >= 0 ? '#526D82' : '#E5453D' }}>
                                    {workingDays >= 0
                                        ? <>총 <strong>{workingDays}</strong>일<span style={{ color: '#aaa', fontSize: '0.75rem', marginLeft: 4 }}>(주말·공휴일 제외)</span></>
                                        : '종료일이 시작일보다 앞에 있습니다.'}
                                </div>
                            )}

                            {/* 사유 */}
                            <div style={fieldS}>
                                <label style={labelS}>사유 <span style={reqS}>*</span></label>
                                <textarea rows={3} placeholder="휴가 사유를 입력하세요"
                                    value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))}
                                    style={{ ...inputS, height: 'auto', padding: '8px 10px', resize: 'vertical' }}
                                    onFocus={e => e.target.style.borderColor = PURPLE}
                                    onBlur={e => e.target.style.borderColor = '#ececf2'}/>
                            </div>

                            {/* 업무 대행자 */}
                            <div style={fieldS}>
                                <label style={labelS}>업무 대행자</label>
                                <div ref={agentDropRef} style={{ position: 'relative' }}>
                                    {form.agentEmail ? (
                                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', border: '1px solid #dee2e6', borderRadius: 8, backgroundColor: '#f8f9fa', fontSize: '0.85rem' }}>
                                            <div style={{ width: 26, height: 26, borderRadius: '50%', backgroundColor: '#526D82', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 600, flexShrink: 0 }}>
                                                {(members.find(m => m.userEmail === form.agentEmail)?.userName || '?').charAt(0)}
                                            </div>
                                            <span style={{ fontWeight: 600, color: '#222' }}>{members.find(m => m.userEmail === form.agentEmail)?.userName || form.agentEmail}</span>
                                            <span style={{ fontSize: '0.75rem', color: '#888' }}>{form.agentEmail}</span>
                                            <button type="button" onClick={() => { setForm(f => ({ ...f, agentEmail: '' })); setAgentSearch(''); }}
                                                style={{ marginLeft: 'auto', width: 18, height: 18, borderRadius: '50%', border: 'none', backgroundColor: '#dc3545', color: '#fff', fontSize: '0.65rem', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
                                        </div>
                                    ) : (
                                        <>
                                            <input placeholder="이름 또는 이메일로 검색" value={agentSearch}
                                                onChange={e => { setAgentSearch(e.target.value); setShowAgentList(true); }}
                                                onFocus={() => setShowAgentList(true)}
                                                autoComplete="off" style={{ ...inputS }}/>
                                            {showAgentList && (
                                                <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 200, backgroundColor: '#fff', border: '1px solid #dee2e6', borderTop: 'none', borderRadius: '0 0 4px 4px', boxShadow: '0 4px 8px rgba(0,0,0,0.08)', maxHeight: 200, overflowY: 'auto' }}>
                                                    {filteredAgents.length === 0
                                                        ? <div style={{ padding: '10px 12px', fontSize: '0.82rem', color: '#aaa', textAlign: 'center' }}>검색 결과가 없습니다.</div>
                                                        : filteredAgents.map(m => memberItem(m, () => { setForm(f => ({ ...f, agentEmail: m.userEmail })); setAgentSearch(''); setShowAgentList(false); }))}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            </div>

                            {/* 긴급 연락처 + 관계 */}
                            <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
                                <div style={{ flex: 1 }}>
                                    <label style={labelS}>긴급 연락처</label>
                                    <input type="tel" placeholder="010-0000-0000" value={form.emergencyContact}
                                        onChange={e => setForm(f => ({ ...f, emergencyContact: e.target.value }))}
                                        style={{ ...inputS }}
                                        onFocus={e => e.target.style.borderColor = PURPLE}
                                        onBlur={e => e.target.style.borderColor = '#ececf2'}/>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label style={labelS}>관계</label>
                                    <input type="text" placeholder="예) 배우자, 부모" value={form.emergencyRelation}
                                        onChange={e => setForm(f => ({ ...f, emergencyRelation: e.target.value }))}
                                        style={{ ...inputS }}
                                        onFocus={e => e.target.style.borderColor = PURPLE}
                                        onBlur={e => e.target.style.borderColor = '#ececf2'}/>
                                </div>
                            </div>

                            {/* 비고 */}
                            <div style={fieldS}>
                                <label style={labelS}>비고</label>
                                <textarea rows={2} placeholder="기타 사항 (선택)" value={form.remark}
                                    onChange={e => setForm(f => ({ ...f, remark: e.target.value }))}
                                    style={{ ...inputS, height: 'auto', padding: '8px 10px', resize: 'vertical' }}
                                    onFocus={e => e.target.style.borderColor = PURPLE}
                                    onBlur={e => e.target.style.borderColor = '#ececf2'}/>
                            </div>

                            {/* 알람 설정 */}
                            <div style={{ marginBottom: 8 }}>
                                <label style={labelS}>알람 설정</label>
                                <div style={{ border: '1px solid #dee2e6', borderRadius: 8, overflow: 'hidden', fontSize: '0.85rem' }}>
                                    <div style={{ padding: '8px 12px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                                        <span style={{ fontWeight: 600, color: '#526D82' }}>신청자</span>
                                    </div>
                                    <div style={{ padding: '8px 12px 4px', display: 'flex', gap: '1.5rem', borderBottom: '1px solid #dee2e6' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                                            <input type="checkbox" checked={form.notiApprovalStatus}
                                                onChange={e => setForm(f => ({ ...f, notiApprovalStatus: e.target.checked }))}
                                                style={{ accentColor: PURPLE }}/>
                                            결재 상태 알람
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                                            <input type="checkbox" checked={form.notiDayBefore}
                                                onChange={e => setForm(f => ({ ...f, notiDayBefore: e.target.checked }))}
                                                style={{ accentColor: PURPLE }}/>
                                            일정 하루 전 알람
                                        </label>
                                    </div>
                                    <div style={{ padding: '8px 12px', backgroundColor: '#f8f9fa', borderBottom: '1px solid #dee2e6' }}>
                                        <span style={{ fontWeight: 600, color: '#526D82' }}>조직원</span>
                                    </div>
                                    <div style={{ padding: '8px 12px 4px', display: 'flex', gap: '1.5rem' }}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                                            <input type="checkbox" checked={form.notiApprover}
                                                onChange={e => setForm(f => ({ ...f, notiApprover: e.target.checked }))}
                                                style={{ accentColor: PURPLE }}/>
                                            결재자 알람
                                        </label>
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div style={{
                    padding: '12px 20px', borderTop: '1px solid #ececf2',
                    display: 'flex', justifyContent: 'flex-end', gap: 8, flexShrink: 0,
                }}>
                    {[
                        { label: deleting ? '삭제 중...' : '삭제', onClick: handleDelete, style: { background: 'white', border: '1.5px solid #fca5a5', color: '#dc2626' } },
                        { label: '취소', onClick: onHide, style: { background: 'white', border: '1.5px solid #ececf2', color: '#5d5b6e' } },
                        { label: copying ? '복사 중...' : '복사하여 임시 저장', onClick: handleCopy, style: { background: 'white', border: '1.5px solid #ececf2', color: '#5d5b6e' } },
                        { label: saving ? '저장 중...' : '임시 저장', onClick: handleSave, style: { background: 'white', border: `1.5px solid ${PURPLE}`, color: PURPLE } },
                        { label: submitting ? '제출 중...' : '제출', onClick: handleSubmit, style: { background: `linear-gradient(135deg, ${PURPLE}, #9b8de8)`, border: 'none', color: 'white' } },
                    ].map(({ label, onClick, style }) => (
                        <button key={label} onClick={onClick} disabled={busy || loadingDetail} style={{
                            height: 34, padding: '0 14px', borderRadius: 8, fontSize: 13,
                            fontWeight: 600, cursor: (busy || loadingDetail) ? 'default' : 'pointer',
                            fontFamily: 'inherit', opacity: (busy || loadingDetail) ? 0.6 : 1,
                            ...style,
                        }}>{label}</button>
                    ))}
                </div>
            </div>
        </div>
    );
}
